import { SidebarButton } from "@/components/SidebarButton";
import { VisibilityControlledByFeatureFlag } from "@/components/VisibilityControlledByFeatureFlag";
import { useFlagSettingsSplitEnabled } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import type { MenuItem } from "@/menu-item";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";
import { useLocation } from "react-router-dom";

export const SettingsMenu: FC<{ menu: MenuItem[] }> = ({ menu }) => {
    const { membership } = useMembership();
    const { data: organization } = useOrganization();
    const { value: settingsSplitEnabled } = useFlagSettingsSplitEnabled();

    const tier = organization?.tier;
    const isTierLoaded = !!tier;
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const items = menu.filter((entry) => {
        let show = true;
        // Hide "My account" section items - these are now in the UserSettingsModal if the feature flag is enabled
        if (entry.section === "My account" && settingsSplitEnabled) {
            return false;
        }
        // If the item is an admin item, only show it if the user is an admin
        if (entry.isAdminRole) {
            show = isOrgAdmin;
        }
        // If the item is hidden on a tier, only show it if the user is not on that tier
        if (entry.hiddenOnTiers && entry.hiddenOnTiers.length > 0) {
            show = show && isTierLoaded && !entry.hiddenOnTiers.includes(tier);
        }
        // If the item has a hidden property, respect it
        if (entry.hidden) {
            const hidden = entry.hidden();
            show = show && !hidden.loading && !hidden.value;
        }
        // If the item is not hidden on a tier, show it
        return show;
    });
    const sections = [...new Set(items.map((item) => item.section || ""))];
    const itemsBySection = new Map(
        sections.map((section) => [section, items.filter((item) => item.section === section)]),
    );

    return (
        <div className="flex flex-col gap-4 pl-2" data-testid="settings-menu">
            {sections.map((section) => (
                <div key={section} className="flex w-full flex-col gap-1" translate="no">
                    <div className="h-8 select-none p-2 text-sm font-bold text-content-secondary">
                        {section === "Organization" ? "Organization Settings" : section}
                    </div>
                    {itemsBySection.get(section)?.map((item) => {
                        const menuItem = <SettingsMenuItem label={item.label} to={item.to} key={item.to} />;
                        return item.featureFlag ? (
                            <VisibilityControlledByFeatureFlag featureFlag={item.featureFlag} key={item.to}>
                                {menuItem}
                            </VisibilityControlledByFeatureFlag>
                        ) : (
                            menuItem
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const SettingsMenuItem: FC<{ label: string; to: string }> = ({ label, to }) => {
    const location = useLocation();
    const active = location.pathname.includes(to);

    return <SidebarButton label={label} to={to} active={active} hideLabel={false} translate="no" />;
};
